import { ISurveyQuestionGroupItem, ISurveyResponseItem, IVisibilityRule } from '../../../../shared/services/survey/survey.model';

export function hasFiniteNumericAnswer(answer: ISurveyResponseItem): boolean {
  return answer.numericValue != null && Number.isFinite(Number(answer.numericValue));
}

function numericAnswerMatchesCondition(answer: ISurveyResponseItem, value: string | number): boolean {
  if (!hasFiniteNumericAnswer(answer)) return false;
  const nv = Number(answer.numericValue);
  const vv = typeof value === 'number' ? value : Number(value as string);
  if (Number.isFinite(vv) && nv === vv) return true;
  return String(answer.numericValue) === String(value);
}

/** Client-side display-condition evaluation; the server stores whatever is posted, so this IS the gate. */
export function evaluateRule(rule: IVisibilityRule | null | undefined, answers: Map<string, ISurveyResponseItem>): boolean {
  if (!rule || !rule.allOf || rule.allOf.length === 0) return true;
  return rule.allOf.every(condition => {
    const answer = answers.get(condition.questionId);
    if (!answer) return false;
    const { op, value } = condition;
    switch (op) {
      case 'answered':
        return !!(
          answer.textValue ||
          hasFiniteNumericAnswer(answer) ||
          answer.booleanValue !== undefined ||
          (answer.optionIds && answer.optionIds.length > 0)
        );
      case 'equals':
        return (
          answer.textValue === value ||
          (answer.optionIds ?? []).includes(value as string) ||
          numericAnswerMatchesCondition(answer, value as string | number)
        );
      case 'notEquals':
        return (
          answer.textValue !== value &&
          !(answer.optionIds ?? []).includes(value as string) &&
          !numericAnswerMatchesCondition(answer, value as string | number)
        );
      case 'in':
        return (value as string[]).some(v =>
          answer.textValue === v ||
          (answer.optionIds ?? []).includes(v) ||
          numericAnswerMatchesCondition(answer, v),
        );
      case 'gt':
        return (answer.numericValue ?? 0) > (value as number);
      case 'lt':
        return (answer.numericValue ?? 0) < (value as number);
      case 'gte':
        return (answer.numericValue ?? 0) >= (value as number);
      case 'lte':
        return (answer.numericValue ?? 0) <= (value as number);
      default:
        return true;
    }
  });
}

export function isItemAnswered(item: ISurveyQuestionGroupItem, answers: Map<string, ISurveyResponseItem>): boolean {
  const answer = answers.get(item.surveyQuestionId);
  if (!answer) return false;
  const type = item.surveyQuestion?.surveyQuestionType?.code;
  const q = item.surveyQuestion;
  if (type === 'TEXT_SINGLE_LINE' || type === 'TEXT_MULTI_LINE') {
    if (q?.enforceNumericAnswer) return hasFiniteNumericAnswer(answer);
    return !!answer.textValue?.trim();
  }
  if (type === 'NUMBER') return hasFiniteNumericAnswer(answer);
  if (type === 'DATE') return !!answer.textValue?.trim();
  if (type === 'SELECT_SINGLE' || type === 'RADIO') return (answer.optionIds ?? []).length > 0;
  if (type === 'SELECT_MULTI' || type === 'CHECKBOX_GROUP') return (answer.optionIds ?? []).length > 0;
  return false;
}

/** True when a numeric answer respects the question's min/max bounds (or there is nothing to check). */
export function isItemInRange(item: ISurveyQuestionGroupItem, answers: Map<string, ISurveyResponseItem>): boolean {
  const q = item.surveyQuestion;
  if (!q?.enforceNumericAnswer && q?.surveyQuestionType?.code !== 'NUMBER') return true;
  const ans = answers.get(item.surveyQuestionId);
  if (!ans || ans.numericValue == null) return true;
  const v = Number(ans.numericValue);
  if (q.minValue != null && v < q.minValue) return false;
  if (q.maxValue != null && v > q.maxValue) return false;
  return true;
}

export function isItemRequired(item: ISurveyQuestionGroupItem): boolean {
  return Boolean(item.requiredOverride ?? item.surveyQuestion?.isRequired);
}
